import React, { useState, useEffect, useCallback } from 'react';
import { message, Row, Form, Tabs } from 'antd';
import { ThunderboltOutlined, AppstoreOutlined, PlusOutlined, RocketOutlined, SwapOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import dayjs from 'dayjs';
import apiClient from '../../server/server';
import EventScrapingSection from './EventScrapingSection';
import EventFormSection from './EventFormSection';
import BatchEventScraper from './BatchEventScraper';
import EventMiniScraper from './EventMiniScraper';
import EdmTrainTab from './EdmTrainTab';
import RAComparatorTab from './RAComparatorTab';
import ProfileEventsViewer from '../../components/ProfileEventsViewer';
import { getActiveCountries } from '../../server/configuration/FiltersServer';
import { COUNTRY_MAPPINGS, CITY_MAPPINGS } from '../../constants/locationMappings';

const EventScraper = ({ profiles, profilesLoading, musicGenres, searchProfiles }) => {
  const [loading, setLoading] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [form] = Form.useForm();
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [hasScrapedAddress, setHasScrapedAddress] = useState(false);
  const [mediaUploadRef, setMediaUploadRef] = useState(null);
  const [videoStoryUploadRef, setVideoStoryUploadRef] = useState(null);
  const [activeTab, setActiveTab] = useState('1');
  const [currentRaEventId, setCurrentRaEventId] = useState(null);
  
  // Search state
  const [searchResults, setSearchResults] = useState(profiles || []);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // Countries and cities state
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(null);

  // Default country/city for all scraped events
  const [defaultCountry, setDefaultCountry] = useState(null);
  const [defaultCity, setDefaultCity] = useState(null);
  const [defaultCities, setDefaultCities] = useState([]);
  
  // AI description generation state
  const [aiLoading, setAiLoading] = useState(false);
  
  // Media upload state to prevent duplicates
  const [mediaUploadInProgress, setMediaUploadInProgress] = useState(false);

  // Debounced search function
  const debouncedSearch = useCallback(async (searchTerm) => {
    if (!searchProfiles) return;
    
    setSearchLoading(true);
    try {
      const results = await searchProfiles(searchTerm);
      setSearchResults(results);
    } catch (error) {
      message.error('Failed to search profiles');
    } finally {
      setSearchLoading(false);
    }
  }, [searchProfiles]);

  const handleProfileSearch = (value) => {
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // If empty search, show initial profiles
    if (!value || value.trim() === '') {
      setSearchResults(profiles);
      setSearchLoading(false);
      return;
    }
    
    // Set loading state immediately
    setSearchLoading(true);
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      debouncedSearch(value.trim());
    }, 300); // 300ms debounce
    
    setSearchTimeout(timeout);
  };

  // Load countries for event creation
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getActiveCountries();
        console.log('🌍 [EventScraper] Countries response:', response);
        // Handle both response.data and response.data.data structures
        const countriesData = response?.data?.data || response?.data || [];
        setCountries(countriesData);
        console.log('🌍 [EventScraper] Loaded countries:', countriesData.length);
      } catch (error) {
        console.error('Failed to load countries:', error);
        message.error('Failed to load countries');
      }
    };
    loadCountries();
  }, []);

  // Update search results when initial profiles change
  useEffect(() => {
    if (profiles && profiles.length > 0 && searchResults.length === 0) {
      setSearchResults(profiles);
    }
  }, [profiles]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handleProfileSelect = (value) => {
    if (!value) {
      setSelectedProfile(null);
      return;
    }

    const selected = searchResults.find(p => p._id === value);
    setSelectedProfile(selected);

    console.log('👤 [Profile Select] Selected profile:', selected?.username, selected);

    // Auto-select music types from the selected profile if event music types are empty
    const currentMusicTypes = form.getFieldValue('musicTypes') || [];
    if (currentMusicTypes.length === 0 && selected?.musicTypes?.length > 0) {
      form.setFieldsValue({ musicTypes: selected.musicTypes });
      console.log('🎵 [Profile Select] Auto-filled music types:', selected.musicTypes);
    }

    // Auto-populate address/location from the selected profile if not already set
    const currentLocation = form.getFieldValue('localisation');
    if ((!currentLocation || !currentLocation.address) && selected?.location) {
      console.log('🏠 [Profile Select] Auto-filling address from profile:', selected.location);
      form.setFieldsValue({
        localisation: {
          address: selected.location,
          coordinates: [0, 0]
        }
      });
      message.success(`Auto-filled address: ${selected.location}`);
    }

    // Auto-select country and city from the selected profile if not already set
    const currentCountry = form.getFieldValue('country');
    const currentCity = form.getFieldValue('city');

    console.log('🌍 [Profile Select] Current country:', currentCountry, 'Current city:', currentCity);
    console.log('🌍 [Profile Select] Profile country:', selected?.country, 'Profile city:', selected?.city);

    if (!currentCountry && selected?.country) {
      // Find the country in our countries list
      const profileCountry = countries.find(c =>
        c._id === selected.country._id ||
        c._id === selected.country ||
        c.name === selected.country.name ||
        c.code === selected.country.code
      );

      if (profileCountry) {
        console.log('✅ [Profile Select] Found matching country:', profileCountry.name);
        form.setFieldsValue({ country: profileCountry._id });
        setSelectedCountryId(profileCountry._id);
        setCities(profileCountry.cities || []);

        // Auto-select city if profile has one and it exists in the country's cities
        if (!currentCity && selected?.city && profileCountry.cities) {
          const profileCity = profileCountry.cities.find(city =>
            city._id === selected.city._id ||
            city._id === selected.city ||
            city.name === selected.city.name
          );

          if (profileCity) {
            console.log('✅ [Profile Select] Found matching city:', profileCity.name);
            form.setFieldsValue({ city: profileCity._id });
            message.success(`Auto-filled location: ${profileCity.name}, ${profileCountry.name}`);
          } else {
            message.success(`Auto-filled country: ${profileCountry.name}`);
          }
        } else {
          message.success(`Auto-filled country: ${profileCountry.name}`);
        }
      } else {
        console.warn('⚠️ [Profile Select] Could not find matching country in countries list');
      }
    } else if (currentCountry) {
      console.log('ℹ️ [Profile Select] Country already set, skipping auto-fill');
    } else if (!selected?.country) {
      console.log('ℹ️ [Profile Select] Profile has no country data');
    }
  };

  const handleCountryChange = (countryId) => {
    const selectedCountry = countries.find(country => country._id === countryId);
    if (selectedCountry) {
      setSelectedCountryId(countryId);
      setCities(selectedCountry.cities || []);
      form.setFieldsValue({ city: null }); // Reset city when country changes
    }
  };

  // Handle default country change for scraper
  const handleDefaultCountryChange = (countryId) => {
    setDefaultCountry(countryId);
    setDefaultCity(null); // Reset default city when country changes

    if (countryId) {
      const selectedCountry = countries.find(country => country._id === countryId);
      if (selectedCountry) {
        setDefaultCities(selectedCountry.cities || []);
      }
    } else {
      setDefaultCities([]);
    }
  };

  // Handle default city change for scraper
  const handleDefaultCityChange = (cityId) => {
    setDefaultCity(cityId);
  };

  // Extract country and city from resolved address and map to database IDs
  const extractLocationFromAddress = (addressString) => {
    if (!addressString || !countries.length) return { countryId: null, cityId: null };

    // Use imported country mappings
    const countryMappings = COUNTRY_MAPPINGS;

    // Parse address components (usually format: "Street, City, Province/State Code Country")
    const parts = addressString.split(', ').map(part => part.trim());
    
    let countryName = null;
    let cityName = null;
    let countryCode = null;

    // Extract country (usually last part)
    if (parts.length > 0) {
      const lastPart = parts[parts.length - 1];
      countryName = lastPart;
      countryCode = countryMappings[lastPart] || lastPart;
    }

    // Extract city (usually second to last, or before province/state)
    if (parts.length >= 3) {
      // For format: "Street, City, Province/State Code Country"
      cityName = parts[parts.length - 3];
    } else if (parts.length >= 2) {
      // For format: "City, Country"
      cityName = parts[parts.length - 2];
    }

    // Use imported city mappings
    const cityMappings = CITY_MAPPINGS;

    // Normalize city name
    if (cityName && cityMappings[cityName]) {
      cityName = cityMappings[cityName];
    }

    // Find country in database
    let foundCountry = null;
    if (countryCode) {
      foundCountry = countries.find(c => 
        c.code === countryCode || 
        c.name.toLowerCase() === countryName?.toLowerCase()
      );
    }

    if (!foundCountry && countryName) {
      foundCountry = countries.find(c => 
        c.name.toLowerCase() === countryName.toLowerCase()
      );
    }

    let countryId = null;
    let cityId = null;

    if (foundCountry) {
      countryId = foundCountry._id;
      
      // Find city within the found country
      if (cityName && foundCountry.cities) {
        // Try exact match first
        let foundCity = foundCountry.cities.find(city => 
          city.name.toLowerCase() === cityName.toLowerCase()
        );
        
        // If no exact match, try normalized matching (remove accents, spaces, etc.)
        if (!foundCity) {
          const normalizeString = (str) => {
            return str.toLowerCase()
              .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove accents
              .replace(/[^a-z0-9]/g, ''); // Remove non-alphanumeric characters
          };
          
          const normalizedCityName = normalizeString(cityName);
          foundCity = foundCountry.cities.find(city => 
            normalizeString(city.name) === normalizedCityName
          );
        }
        
        // If still no match, try partial matching
        if (!foundCity) {
          foundCity = foundCountry.cities.find(city => 
            city.name.toLowerCase().includes(cityName.toLowerCase()) ||
            cityName.toLowerCase().includes(city.name.toLowerCase())
          );
        }
        
        if (foundCity) {
          cityId = foundCity._id;
        }
      }
    }

    return { countryId, cityId };
  };

  // Handle address input change to automatically set country/city
  const handleAddressChange = (addressData) => {
    // Update form with the address data
    form.setFieldsValue({
      localisation: addressData.localisation || addressData
    });

    // Extract and set country/city automatically
    const addressString = addressData.localisation?.address || addressData.address;
    if (addressString) {
      const { countryId, cityId } = extractLocationFromAddress(addressString);
      
      if (countryId) {
        // Update country field
        form.setFieldsValue({ country: countryId });
        
        // Update country state and load cities
        const selectedCountry = countries.find(c => c._id === countryId);
        if (selectedCountry) {
          setSelectedCountryId(countryId);
          setCities(selectedCountry.cities || []);
          
          // Update city field if we found a match
          if (cityId) {
            form.setFieldsValue({ city: cityId });
          }
          
          // Show success message for auto-population
          const selectedCity = selectedCountry.cities?.find(city => city._id === cityId);
          if (selectedCity) {
            message.success(`✅ Auto-populated location: ${selectedCity.name}, ${selectedCountry.name}`);
          } else {
            message.success(`✅ Auto-populated country: ${selectedCountry.name}`);
          }
        }
      }
    }
  };

  // Extract RA event ID from URL
  const extractRaEventId = (url) => {
    if (!url) return null;
    // RA URLs are typically: https://ra.co/events/XXXXXX or https://www.ra.co/events/XXXXXX
    const match = url.match(/ra\.co\/events\/(\d+)/i);
    return match ? match[1] : null;
  };

  // Common scraping logic
  const handleEventScraping = async (url, scraperType = 'ra', providedRaEventId = null) => {
    try {
      setLoading(true);

      // Extract RA event ID from URL for RA scraper
      if (scraperType === 'ra') {
        // Use provided raEventId first (from RA Comparator), otherwise extract from URL
        const raEventId = providedRaEventId || extractRaEventId(url);
        console.log('🔗 [EventScraper] RA Event ID - Provided:', providedRaEventId, 'Extracted:', extractRaEventId(url), 'Using:', raEventId);
        setCurrentRaEventId(raEventId || null);
      }

      let endpoint = '/scrape'; // default for RA
      if (scraperType === 'tixr') {
        endpoint = '/tixr/scrape';
      } else if (scraperType === 'tickit') {
        endpoint = '/tickit/scrape';
      }
      const response = await axios.post(`http://localhost:3002${endpoint}`, { url });
      console.log('🔍 [EventScraper] Raw scraped data:', response.data);
      setEventData(response.data);
      message.success(`${scraperType.toUpperCase()} event scraped successfully!`);

      // Map scraped genres to IDs
      const scrapedGenres = response.data.genres || response.data.lineup || [];
      const genreNameToId = {};
      musicGenres.forEach(g => { genreNameToId[g.value.toLowerCase()] = g._id; });
      const musicTypeIds = scrapedGenres
        .map(g => genreNameToId[g.toLowerCase()])
        .filter(Boolean);

      // Process dates - just use moment normally
      let startTime = null;
      if (response.data.dateTime) {
        console.log('🕐 [EventScraper] Original startTime:', response.data.dateTime);
        startTime = moment(response.data.dateTime);
        if (!startTime.isValid()) {
          console.log('❌ [EventScraper] Invalid startTime, using current time');
          startTime = moment();
        } else {
          console.log('✅ [EventScraper] Parsed startTime:', startTime.format('YYYY-MM-DD HH:mm:ss'));
        }
      }
      
      let endTime = null;
      if (response.data.endDateTime) {
        console.log('🕐 [EventScraper] Original endTime:', response.data.endDateTime);
        endTime = moment(response.data.endDateTime);
        if (!endTime.isValid()) {
          console.log('❌ [EventScraper] Invalid endTime, using startTime + 6 hours');
          if (startTime) {
            endTime = startTime.clone().add(6, 'hours');
          }
        } else {
          console.log('✅ [EventScraper] Parsed endTime:', endTime.format('YYYY-MM-DD HH:mm:ss'));
        }
      } else if (startTime) {
        console.log('⏰ [EventScraper] No endDateTime provided, using startTime + 6 hours');
        endTime = startTime.clone().add(6, 'hours');
      }
      
      const publishTime = moment();

      // Auto-search for creator profile based on location or promoter
      const searchName = response.data.location || response.data.promoter || response.data.organizer || '';
      let autoSelectedProfileId = null;

      if (searchName && searchProfiles) {
        try {
          console.log('🔍 [EventScraper] Auto-searching for creator profile:', searchName);
          const profileResults = await searchProfiles(searchName);

          if (profileResults && profileResults.length > 0) {
            console.log('🔍 [EventScraper] Found profiles:', profileResults.map(p => p.username));

            // Normalize for comparison
            const normalize = (str) => str?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';
            const normalizedSearch = normalize(searchName);

            // Try exact match
            const exactMatch = profileResults.find(p =>
              normalize(p.username) === normalizedSearch ||
              normalize(p.displayName) === normalizedSearch
            );

            // Try partial match
            const partialMatch = !exactMatch && profileResults.find(p => {
              const user = normalize(p.username);
              const display = normalize(p.displayName);
              return (user && (normalizedSearch.includes(user) || user.includes(normalizedSearch))) ||
                     (display && (normalizedSearch.includes(display) || display.includes(normalizedSearch)));
            });

            const matchedProfile = exactMatch || partialMatch;

            if (matchedProfile) {
              autoSelectedProfileId = matchedProfile._id;
              setSelectedProfile(matchedProfile);
              setSearchResults(profileResults);

              // Auto-populate music types from matched profile if none from scraper
              if (matchedProfile.musicTypes && matchedProfile.musicTypes.length > 0) {
                const profileMusicTypes = matchedProfile.musicTypes.map(mt =>
                  typeof mt === 'object' ? mt._id : mt
                );
                if (musicTypeIds.length === 0) {
                  musicTypeIds.push(...profileMusicTypes);
                }
              }

              console.log('✅ [EventScraper] Auto-matched:', matchedProfile.username);
              message.success(`Auto-matched: ${matchedProfile.username || matchedProfile.displayName}`);
            } else {
              setSearchResults(profileResults);
              message.warning(`Found ${profileResults.length} profiles for "${searchName}". Please select.`);
            }
          } else {
            message.info(`No profile found for "${searchName}". Please select manually.`);
          }
        } catch (error) {
          console.error('❌ [EventScraper] Error auto-searching profiles:', error);
        }
      }

      // Pre-fill the form with scraped data
      const formValues = {
        title: response.data.name,
        description: response.data.description,
        organisationName: response.data.promoter || response.data.organizer || '',
        tags: response.data.lineup || [],
        startTime: startTime,
        endTime: endTime,
        publishTime: publishTime,
        city: null,
        country: null,
        cover: response.data.image,
        flyer: response.data.image,
        musicTypes: musicTypeIds,
        eventType: 'Club',
        media: [],
        musicList: [],
        videoStory: '',
        embedmix: '',
        youtubeSet: '',
        externalLink: url, // Add the original scraped URL to external link field
        localisation: {
          address: response.data.address || response.data.location || '',
          coordinates: [0, 0]
        },
        creatorProfileId: autoSelectedProfileId // Auto-set the matched profile
      };

      console.log('📋 [EventScraper] Final form values:', {
        startTime: formValues.startTime?.format('YYYY-MM-DD HH:mm:ss'),
        endTime: formValues.endTime?.format('YYYY-MM-DD HH:mm:ss'),
        title: formValues.title
      });
      
      // Convert moment to dayjs for Ant Design DatePicker
      const dayjsStartTime = startTime ? dayjs(startTime.format('YYYY-MM-DD HH:mm:ss')) : null;
      const dayjsEndTime = endTime ? dayjs(endTime.format('YYYY-MM-DD HH:mm:ss')) : null;
      
      console.log('🔄 [EventScraper] Converting to dayjs:', {
        startTime: dayjsStartTime?.format('YYYY-MM-DD HH:mm:ss'),
        endTime: dayjsEndTime?.format('YYYY-MM-DD HH:mm:ss')
      });
      
      // Get the raEventId that was set earlier in this function
      const currentRaId = providedRaEventId || extractRaEventId(url);

      // Set form values with dayjs objects
      form.setFieldsValue({
        ...formValues,
        startTime: dayjsStartTime,
        endTime: dayjsEndTime,
        raEventId: currentRaId || ''
      });
      
      // Debug: Check what the form actually has after setting values
      setTimeout(() => {
        const formStartTime = form.getFieldValue('startTime');
        const formEndTime = form.getFieldValue('endTime');
        console.log('🔍 [EventScraper] Form times after setFieldsValue:', {
          startTime: formStartTime?.format ? formStartTime.format('YYYY-MM-DD HH:mm:ss') : 'Not a moment object',
          endTime: formEndTime?.format ? formEndTime.format('YYYY-MM-DD HH:mm:ss') : 'Not a moment object'
        });
      }, 100);
      setHasScrapedAddress(Boolean(response.data.address || response.data.location));
      setShowEventForm(true);

      // Auto-populate country/city/location from default filters, matched profile, OR scraped address
      setTimeout(() => {
        let countryId = null;
        let cityId = null;
        let shouldUpdateLocation = false;

        // First priority: Use default country/city from scraper filters
        if (defaultCountry) {
          countryId = defaultCountry;
          form.setFieldsValue({ country: countryId });
          setSelectedCountryId(countryId);
          setCities(defaultCities || []);

          if (defaultCity) {
            cityId = defaultCity;
            form.setFieldsValue({ city: cityId });
          }
        }
        // Second priority: Use matched profile's country/city/location
        else if (selectedProfile) {
          // Auto-populate country from profile
          if (selectedProfile.country) {
            const profileCountry = countries.find(c =>
              c._id === selectedProfile.country._id ||
              c._id === selectedProfile.country ||
              c.name === selectedProfile.country.name ||
              c.code === selectedProfile.country.code
            );

            if (profileCountry) {
              countryId = profileCountry._id;
              form.setFieldsValue({ country: countryId });
              setSelectedCountryId(countryId);
              setCities(profileCountry.cities || []);

              // Auto-populate city from profile
              if (selectedProfile.city && profileCountry.cities) {
                const profileCity = profileCountry.cities.find(city =>
                  city._id === selectedProfile.city._id ||
                  city._id === selectedProfile.city ||
                  city.name === selectedProfile.city.name
                );

                if (profileCity) {
                  cityId = profileCity._id;
                  form.setFieldsValue({ city: cityId });
                }
              }
            }
          }

          // Auto-populate location/address from profile if not already from scraper
          if (selectedProfile.location && !response.data.address) {
            form.setFieldsValue({
              localisation: {
                address: selectedProfile.location,
                coordinates: [0, 0]
              }
            });
            shouldUpdateLocation = true;
          }
        }

        // Third priority: Use scraped address if no default and profile didn't provide country/city
        if (!countryId && (response.data.address || response.data.location)) {
          const scrapedAddress = response.data.address || response.data.location;
          const extracted = extractLocationFromAddress(scrapedAddress);

          if (extracted.countryId) {
            countryId = extracted.countryId;
            form.setFieldsValue({ country: countryId });
            const selectedCountry = countries.find(c => c._id === countryId);
            if (selectedCountry) {
              setSelectedCountryId(countryId);
              setCities(selectedCountry.cities || []);
            }
          }
          if (extracted.cityId) {
            cityId = extracted.cityId;
            form.setFieldsValue({ city: cityId });
          }
        }

        // Show success message
        const countryName = countries.find(c => c._id === countryId)?.name;
        const cityName = countries.find(c => c._id === countryId)?.cities?.find(city => city._id === cityId)?.name;
        if (countryName && cityName) {
          message.success(`✅ Auto-populated location: ${cityName}, ${countryName}`);
        } else if (countryName) {
          message.success(`✅ Auto-populated country: ${countryName}`);
        }
      }, 100);
    } catch (error) {
      message.error(`Failed to scrape ${scraperType.toUpperCase()} event: ` + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const onFinishEvent = async (values) => {
    await handleEventScraping(values.url, 'ra');
  };

  const onFinishTixrEvent = async (values) => {
    await handleEventScraping(values.tixrUrl, 'tixr');
  };

  const onFinishTickitEvent = async (values) => {
    await handleEventScraping(values.tickitUrl, 'tickit');
  };

  // AI description generation function
  const generateAIDescription = async () => {
    try {
      setAiLoading(true);
      
      const currentValues = form.getFieldsValue();
      const title = currentValues.title || '';
      const eventType = currentValues.eventType || '';
      const musicTypes = currentValues.musicTypes || [];
      const tags = currentValues.tags || [];
      const organisationName = currentValues.organisationName || '';
      
      const keywords = [
        title,
        eventType,
        ...musicTypes,
        ...tags,
        organisationName
      ].filter(Boolean).join(', ');
      
      const venue = currentValues.localisation?.address || '';
      
      const response = await axios.get('https://mysway.app/api/ai/generate-event-description', {
        params: {
          keywords: keywords || 'electronic music event',
          venue: venue,
          eventType: eventType || 'Club'
        }
      });
      
      if (response.data && response.data.description) {
        form.setFieldsValue({ description: response.data.description });
        message.success('AI description generated successfully!');
      } else {
        message.error('Failed to generate AI description');
      }
    } catch (error) {
      message.error('Failed to generate AI description. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreateEvent = async (values) => {
    try {
      
      // Prevent multiple simultaneous uploads
      if (mediaUploadInProgress) {
        message.warning('Media upload already in progress. Please wait...');
        return;
      }
      
      setLoading(true);
      setMediaUploadInProgress(true);
      
      // First, upload any pending media files
      let mediaUrls = [];
      if (mediaUploadRef && mediaUploadRef.uploadPendingFiles) {
        try {
          mediaUrls = await mediaUploadRef.uploadPendingFiles();
          // Remove duplicates
          mediaUrls = [...new Set(mediaUrls.filter(Boolean))];
        } catch (mediaError) {
          message.error('Failed to upload media files. Please try again.');
          setLoading(false);
          setMediaUploadInProgress(false);
          return;
        }
      }

      // Upload video story if present
      let videoStoryUrl = values.videoStory || '';
      if (videoStoryUploadRef && videoStoryUploadRef.uploadPendingFiles) {
        try {
          const videoStoryUrls = await videoStoryUploadRef.uploadPendingFiles();
          if (videoStoryUrls.length > 0) {
            videoStoryUrl = videoStoryUrls[0];
          }
        } catch (videoError) {
          message.error('Failed to upload video story. Please try again.');
          setLoading(false);
          setMediaUploadInProgress(false);
          return;
        }
      }
      
      let flyerUrl = values.flyer || '';
      
      // If flyer is not from our backend, upload it using the new endpoint
      if (flyerUrl && !flyerUrl.includes('api.decadance.app')) {
        try {
          const requestPayload = {
            imageUrl: flyerUrl,
            timestamp: Date.now(),
            _cacheBust: Math.random()
          };
          
          const uploadResponse = await apiClient.post('/uploads/from-url', requestPayload);
          
          if (uploadResponse.data && uploadResponse.data.data && uploadResponse.data.data.url) {
            flyerUrl = uploadResponse.data.data.url;
          } else {
            throw new Error('Invalid response format from image upload');
          }
        } catch (uploadErr) {
          message.error(`Failed to upload flyer to backend: ${uploadErr.response?.statusText || uploadErr.message}`);
          setLoading(false);
          setMediaUploadInProgress(false);
          return;
        }
      }
      
      const eventPayload = {
        ...values,
        isPublished: true,
        localisation: values.localisation || {
          coordinates: [0, 0],
          address: values.address || '',
        },
        isPrivate: false,
        includeChat: true,
        startTime: values.startTime?.toISOString(),
        endTime: values.endTime?.toISOString(),
        publishTime: values.publishTime?.toISOString(),
        musicTypes: Array.isArray(values.musicTypes) ? values.musicTypes : [],
        tags: Array.isArray(values.tags) ? values.tags : [],
        affiliatedProfiles: Array.isArray(values.affiliatedProfiles) ? values.affiliatedProfiles : [],
        media: [...new Set([...(Array.isArray(values.media) ? values.media : []), ...mediaUrls])], // Combine and deduplicate media
        musicList: Array.isArray(values.musicList) ? values.musicList : [],
        flyer: flyerUrl,
        videoStory: videoStoryUrl,
        externalLink: values.externalLink || '',
        youtubeSet: values.youtubeSet || '',
        embedmix: values.embedmix || '',
        eventType: values.eventType || 'Club',
        description: values.description || '',
        organisationName: values.organisationName || '',
        cover: values.cover || null,
        // Include RA event ID for tracking/comparison - use form value or state
        raEventId: values.raEventId || currentRaEventId || null,
      };

      console.log('📝 [EventScraper] Creating event with raEventId:', eventPayload.raEventId, '(form:', values.raEventId, 'state:', currentRaEventId, ')');

      // Remove any nested localisation objects
      if (eventPayload.localisation?.localisation) {
        eventPayload.localisation = eventPayload.localisation.localisation;
      }

      await apiClient.post('/backoffice/events/create', eventPayload);
      message.success('Event created successfully!');
      setShowEventForm(false);
      setHasScrapedAddress(false);
      setEventData(null);
      setMediaUploadRef(null);
      setVideoStoryUploadRef(null);
      setSelectedProfile(null);
      setSearchResults(profiles || []);
      setCurrentRaEventId(null);
      form.resetFields();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      message.error(`Failed to create event: ${errorMessage}`);
    } finally {
      setLoading(false);
      setMediaUploadInProgress(false);
    }
  };

  const tabItems = [
    {
      key: '1',
      label: (
        <span>
          <ThunderboltOutlined />
          Single Event Scraper
        </span>
      ),
      children: (
        <div>
          {/* Event Scraping Section */}
          <EventScrapingSection
            loading={loading}
            onFinishEvent={onFinishEvent}
            onFinishTixrEvent={onFinishTixrEvent}
            onFinishTickitEvent={onFinishTickitEvent}
            eventData={eventData}
            showEventForm={showEventForm}
            setShowEventForm={setShowEventForm}
            countries={countries}
            cities={defaultCities}
            defaultCountry={defaultCountry}
            defaultCity={defaultCity}
            onDefaultCountryChange={handleDefaultCountryChange}
            onDefaultCityChange={handleDefaultCityChange}
          />

          {showEventForm && (
            <Row gutter={24}>
              <EventFormSection
                form={form}
                loading={loading}
                handleCreateEvent={handleCreateEvent}
                setShowEventForm={setShowEventForm}
                setHasScrapedAddress={setHasScrapedAddress}
                setMediaUploadRef={setMediaUploadRef}
                setVideoStoryUploadRef={setVideoStoryUploadRef}
                selectedProfile={selectedProfile}
                searchResults={searchResults}
                profilesLoading={profilesLoading}
                searchLoading={searchLoading}
                handleProfileSearch={handleProfileSearch}
                handleProfileSelect={handleProfileSelect}
                countries={countries}
                cities={cities}
                handleCountryChange={handleCountryChange}
                handleAddressChange={handleAddressChange}
                hasScrapedAddress={hasScrapedAddress}
                musicGenres={musicGenres}
                aiLoading={aiLoading}
                generateAIDescription={generateAIDescription}
                raEventId={currentRaEventId}
              />
            </Row>
          )}
        </div>
      ),
    },
    {
      key: '2',
      label: (
        <span>
          <AppstoreOutlined />
          Batch Event Scraper
        </span>
      ),
      children: (
        <BatchEventScraper
          profiles={profiles}
          profilesLoading={profilesLoading}
          searchProfiles={searchProfiles}
          musicGenres={musicGenres}
          countries={countries}
          cities={cities}
          handleCountryChange={handleCountryChange}
          handleAddressChange={handleAddressChange}
          selectedCountryId={selectedCountryId}
          setCities={setCities}
          setSelectedCountryId={setSelectedCountryId}
          generateAIDescription={generateAIDescription}
        />
      ),
    },
    {
      key: '3',
      label: (
        <span>
          <PlusOutlined />
          EventMini Creator
        </span>
      ),
      children: (
        <EventMiniScraper
          profiles={profiles}
          profilesLoading={profilesLoading}
          searchProfiles={searchProfiles}
          musicGenres={musicGenres}
        />
      ),
    },
    {
      key: '4',
      label: (
        <span>
          <RocketOutlined />
          EDM Train
        </span>
      ),
      children: (
        <EdmTrainTab
          profiles={profiles}
          profilesLoading={profilesLoading}
          searchProfiles={searchProfiles}
          musicGenres={musicGenres}
        />
      ),
    },
    {
      key: '5',
      label: (
        <span>
          <SwapOutlined />
          RA Comparator
        </span>
      ),
      children: (
        <div>
          <RAComparatorTab
            onScrapeEvent={async (url, cityInfo) => {
              // Pass raEventId directly to handleEventScraping so it's set before the form appears
              const raEventId = cityInfo?.raEventId || null;
              await handleEventScraping(url, 'ra', raEventId);
              // Pre-fill city from comparator selection if provided
              if (cityInfo && cityInfo.countryId && cityInfo.cityId) {
                form.setFieldsValue({
                  country: cityInfo.countryId,
                  city: cityInfo.cityId
                });
                setSelectedCountryId(cityInfo.countryId);
                const selectedCountry = countries.find(c => c._id === cityInfo.countryId);
                if (selectedCountry) {
                  setCities(selectedCountry.cities || []);
                }
              }
            }}
            countries={countries}
          />

          {/* Show event form directly under RA Comparator when scraping */}
          {showEventForm && (
            <Row gutter={24} style={{ marginTop: 24 }}>
              <EventFormSection
                form={form}
                loading={loading}
                handleCreateEvent={handleCreateEvent}
                setShowEventForm={setShowEventForm}
                setHasScrapedAddress={setHasScrapedAddress}
                setMediaUploadRef={setMediaUploadRef}
                setVideoStoryUploadRef={setVideoStoryUploadRef}
                selectedProfile={selectedProfile}
                searchResults={searchResults}
                profilesLoading={profilesLoading}
                searchLoading={searchLoading}
                handleProfileSearch={handleProfileSearch}
                handleProfileSelect={handleProfileSelect}
                countries={countries}
                cities={cities}
                handleCountryChange={handleCountryChange}
                handleAddressChange={handleAddressChange}
                hasScrapedAddress={hasScrapedAddress}
                musicGenres={musicGenres}
                aiLoading={aiLoading}
                generateAIDescription={generateAIDescription}
                raEventId={currentRaEventId}
              />
            </Row>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Profile Events Viewer */}
      <ProfileEventsViewer 
        profiles={profiles}
        profilesLoading={profilesLoading}
        searchProfiles={searchProfiles}
      />

      {/* Main Scraper Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        className="mt-4"
        size="large"
      />
    </div>
  );
};

export default EventScraper;